
import React, { useState, useEffect, useRef } from 'react';
import { Box, Button, Container, Typography, CircularProgress, IconButton, Alert, Snackbar } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import AppleIcon from '@mui/icons-material/Apple';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ImageIcon from '@mui/icons-material/Image';
import { BASE_URL } from '../../config';
import { getOfferDisplayName } from '../../utils/offerMapping';
import { getInAppBrowserName } from '../../utils/inAppBrowser';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import StickyActionBar, { fullScreenHeight } from '../../components/common/StickyActionBar';
import { checkIosScreenshot, compressScreenshot } from '../../utils/iosScreenshot';
import correctExample from '../../assets/screenshot_example_correct.jpg';
import incorrectExample from '../../assets/screenshot_example_incorrect.jpg';

const font = '"Plus Jakarta Sans", sans-serif';

const ScreenshotExample = ({ correct, image, caption }: { correct: boolean; image: string; caption: string }) => {
  const color = correct ? '#15803D' : '#DC2626';
  return (
    <Box sx={{ flex: 1, minWidth: 0, bgcolor: correct ? '#EEF8F1' : '#FDF1F1', border: `1.5px solid ${correct ? '#8FD1A8' : '#F3AAAA'}`, borderRadius: '16px', p: 1, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
        <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          {correct ? <CheckIcon sx={{ color: '#fff', fontSize: 16 }} /> : <CloseIcon sx={{ color: '#fff', fontSize: 16 }} />}
        </Box>
        <Typography sx={{ color, fontWeight: 800, fontSize: '13px', letterSpacing: '0.3px', fontFamily: font }}>
          {correct ? 'CORRECT' : 'INCORRECT'}
        </Typography>
      </Box>
      {/* Frame matches the iPhone screenshot shape (360x780) so the whole screenshot is visible */}
      <Box sx={{ mx: 'auto', width: '72%', aspectRatio: '360 / 780', boxSizing: 'content-box', border: '3px solid #111', borderRadius: '16px', overflow: 'hidden', bgcolor: '#fff', mb: 1 }}>
        <img src={image} alt={correct ? 'Correct screenshot example' : 'Incorrect screenshot example'} style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />
      </Box>
      <Box sx={{ bgcolor: correct ? '#D9F0E1' : '#FADCDC', borderRadius: '10px', px: 1, py: 0.75, mt: 'auto' }}>
        <Typography sx={{ color: '#111', fontSize: '11.5px', fontWeight: 700, lineHeight: 1.3, textAlign: 'center', fontFamily: font }}>
          {caption}
        </Typography>
      </Box>
    </Box>
  );
};

export default function TaskSubmissionPage() {
  const [file, setFile] = useState<File | null>(null);
  const [formName, setFormName] = useState('');
  const [offerAmount, setOfferAmount] = useState('25');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [appUrl, setAppUrl] = useState('');
  const [isScreenshotReceived, setIsScreenshotReceived] = useState(false);
  const [appStoreBlocked, setAppStoreBlocked] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [uploadError, setUploadError] = useState('');
  // Setter unused while screenshot validation is commented out below
  const [isCheckingFile, setIsCheckingFile] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [showOfferWarning, setShowOfferWarning] = useState(false);
  const [highlightUpload, setHighlightUpload] = useState(false);
  const navigate = useNavigate();
  const appStoreCheckTimer = useRef<ReturnType<typeof setTimeout>>();
  const highlightTimer = useRef<ReturnType<typeof setTimeout>>();
  const uploadAreaRef = useRef<HTMLLabelElement>(null);
  const inAppBrowser = getInAppBrowserName();
  // ?offer= lets the page recover its offer after switching from an in-app browser to Safari,
  // which doesn't share localStorage with it
  const [offerNumber] = useState(
    () => new URLSearchParams(window.location.search).get('offer') || localStorage.getItem('current_form_number') || '0'
  );

  useEffect(() => () => {
    clearTimeout(appStoreCheckTimer.current);
    clearTimeout(highlightTimer.current);
  }, []);

  // Tapping the inactive submit button takes the user to the upload area instead
  const showUploadArea = () => {
    uploadAreaRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setHighlightUpload(true);
    clearTimeout(highlightTimer.current);
    highlightTimer.current = setTimeout(() => setHighlightUpload(false), 1500);
  };

  useEffect(() => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const handleFileSelected = async (selected: File) => {
    setErrorMessage('');
    setUploadError('');
    setIsCheckingFile(true);
    const result = await checkIosScreenshot(selected);
    setIsCheckingFile(false);
    if (result.ok) {
      setFile(selected);
    } else {
      setFile(null);
      setPreviewUrl('');
      setUploadError(result.reason);
    }
  };

  // Keep the offer in the address bar so "Open in external browser" carries it to Safari
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('offer') === offerNumber) return;
    params.set('offer', offerNumber);
    window.history.replaceState(window.history.state, '', `${window.location.pathname}?${params}`);
  }, [offerNumber]);

  useEffect(() => {
    // Set by the landing page when user clicks install; ignore it if it belongs to a different offer
    const sameOffer = localStorage.getItem('current_form_number') === offerNumber;
    const storedFormName = sameOffer ? localStorage.getItem('current_form_name') : null;
    const storedAmount = sameOffer ? localStorage.getItem('current_offer_amount') : null;
    const storedAppUrl = sameOffer ? localStorage.getItem('current_app_url') : null;
    if (storedFormName) setFormName(storedFormName);
    if (storedAmount) setOfferAmount(storedAmount);
    if (storedAppUrl) setAppUrl(storedAppUrl);
    if (storedFormName && storedAppUrl) return;

    // Landing page tapped before its API call finished, or storage lost on a browser switch
    fetch(`${BASE_URL}/api/form/check-ios-form/?url=${offerNumber}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        localStorage.setItem('current_form_number', offerNumber);
        if (data?.link) {
          localStorage.setItem('current_app_url', data.link);
          setAppUrl(data.link);
        }
        if (!storedFormName && data?.name) {
          localStorage.setItem('current_form_name', data.name);
          setFormName(data.name);
        }
        if (!storedAmount && data?.amount) {
          localStorage.setItem('current_offer_amount', String(data.amount));
          setOfferAmount(String(data.amount));
        }
        if (!storedFormName && !data?.name) setFormName('Ring Form');
      })
      .catch((err) => {
        console.error('Failed to fetch offer:', err);
        if (!storedFormName) setFormName('Ring Form');
      });
  }, [offerNumber]);

  const handleSubmit = async () => {
    // Clear previous errors
    setErrorMessage('');

    // Validation
    if (!file) {
      showUploadArea();
      return;
    }

    if (!formName) {
      setErrorMessage('Form name not found. Please go back and try again.');
      return;
    }

    setIsSubmitting(true);

    try {
      // Compressed (same pixel size) so it fits in localStorage for the register page
      const { dataUrl, type, name } = await compressScreenshot(file);
      try {
        localStorage.setItem('pending_screenshot', dataUrl);
        localStorage.setItem('pending_screenshot_name', name);
        localStorage.setItem('pending_screenshot_type', type);
        setIsScreenshotReceived(true);
      } catch (e) {
        console.error("Local storage error:", e);
        setErrorMessage('Could not save the screenshot on this device. Please free up some space or try again.');
      }
    } catch (error: any) {
      setErrorMessage(error.message || 'An unexpected error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  // https://apps.apple.com/app/id<APP_ID>, using the app id from the backend link;
  // falls back to the backend link (itms-apps:// rewritten to https://) if it has no id.
  const appStoreId = appUrl.match(/\/id(\d+)/)?.[1];
  const appStoreHref = appStoreId
    ? `https://apps.apple.com/app/id${appStoreId}`
    : appUrl.replace(/^itms-apps?s?:\/\//, 'https://');
  const handoffUrl = `${window.location.origin}/submit-task?offer=${encodeURIComponent(offerNumber)}`;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(handoffUrl);
      setLinkCopied(true);
    } catch {
      window.prompt('Copy this link and open it in Safari:', handoffUrl);
    }
  };

  // Stay inside the funnel: '/' is the landing page, which sends iOS users off to /ios
  const handleBack = () => {
    if (window.history.state?.idx > 0) navigate(-1);
    else navigate('/active-offer', { replace: true });
  };

  return (
    <Box sx={{ ...fullScreenHeight, bgcolor: '#F2F5F1', display: 'flex', flexDirection: 'column' }}>
      
      {isScreenshotReceived ? (
        <>
          {/* Screenshot Received UI */}
          {/* Screenshot Received UI Header */}
          {/* Kept compact so the whole screen fits without scrolling */}
          <Box sx={{ bgcolor: '#0F231A', pt: 3, pb: 4, px: 2, position: 'relative' }}>
            <Container maxWidth="sm" sx={{ p: 0, position: 'relative' }}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', position: 'absolute', top: -20, right: 0 }}>
                <IconButton onClick={() => navigate('/active-offer', { replace: true })} sx={{ color: '#fff', p: 0 }}>
                  <Typography sx={{ fontSize: '24px', fontWeight: 300, lineHeight: 1 }}>×</Typography>
                </IconButton>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', color: '#fff' }}>
                <Box sx={{ width: 44, height: 44, bgcolor: '#C9F23C', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                  <Typography sx={{ color: '#0F231A', fontSize: '24px', fontWeight: 'bold' }}>✓</Typography>
                </Box>

                <Typography sx={{ fontSize: '24px', fontWeight: 700, lineHeight: 1.1, mb: 1, fontFamily: '"Moan Slight", sans-serif', letterSpacing: '-0.5px' }}>
                  Thank you! Screenshot<br/>received
                </Typography>
                
                <Typography sx={{ color: '#A5D6B6', fontSize: '14px', fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
                  ₹{offerAmount} is pending. We'll verify it within 24 hours.
                </Typography>
              </Box>
            </Container>
          </Box>
          
          {/* Light Green Body with Rounded Top Corners */}
          <Box sx={{ 
            bgcolor: '#F2F5F1', 
            borderTopLeftRadius: '32px', 
            borderTopRightRadius: '32px', 
            mt: -4, 
            pt: 2, 
            flexGrow: 1, 
            position: 'relative', 
            zIndex: 2 
          }}>
            <Container maxWidth="sm" sx={{ px: 2, pb: 'calc(12px + env(safe-area-inset-bottom))' }}>
              {/* Register Card */}
            <Box sx={{ bgcolor: '#fff', borderRadius: '24px', p: 2, mb: 2, border: '2px solid #0F763F', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
              <Box sx={{ display: 'flex', gap: 1.5, mb: 1, alignItems: 'flex-start' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.3 }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="2" y="5" width="20" height="14" rx="3" stroke="#0F763F" strokeWidth="2.5"/>
                    <path d="M2 10.5H22" stroke="#0F763F" strokeWidth="2.5"/>
                    <path d="M7 15.5H11" stroke="#0F763F" strokeWidth="2.5" strokeLinecap="round"/>
                  </svg>
                </Box>
                <Typography sx={{ color: '#0F231A', fontSize: '18px', fontWeight: 800, fontFamily: '"Plus Jakarta Sans", sans-serif', letterSpacing: '-0.3px' }}>
                  Register to get your ₹{offerAmount}
                </Typography>
              </Box>
              <Typography sx={{ color: '#666', fontSize: '14px', mb: 1.25, pl: 4, fontFamily: '"Plus Jakarta Sans", sans-serif', lineHeight: 1.4 }}>
                Add your UPI ID so we know where to send it. Takes 30 seconds.
              </Typography>
              <Button
                variant="contained"
                fullWidth
                onClick={() => window.location.href = '/register'}
                sx={{
                  bgcolor: '#0F763F !important',
                  color: '#fff',
                  py: 1.4,
                  borderRadius: '14px',
                  fontSize: '16px',
                  fontWeight: 700,
                  textTransform: 'none',
                  boxShadow: 'none',
                  fontFamily: '"Plus Jakarta Sans", sans-serif',
                  '&:hover': { bgcolor: '#0d6535 !important', boxShadow: 'none' }
                }}
              >
                Get Money
              </Button>
            </Box>
            
            <Typography sx={{ color: '#000', fontSize: '20px', fontWeight: 700, mb: 1, fontFamily: '"Moan Slight", sans-serif' }}>
              More offers for you
            </Typography>
            
            <Box sx={{ bgcolor: '#fff', borderRadius: '24px', overflow: 'hidden' }}>
              {/* Offer B */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1.25, borderBottom: '1px solid #F0F0F0' }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Box sx={{ width: 38, height: 38, bgcolor: '#3955A0', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 'bold', fontSize: '18px', fontFamily: '"Plus Jakarta Sans", sans-serif' }}>B</Box>
                  <Box>
                    <Typography sx={{ color: '#000', fontWeight: 800, fontSize: '15px', fontFamily: '"Plus Jakarta Sans", sans-serif' }}>Offer B • ₹40</Typography>
                    <Typography sx={{ color: '#666', fontSize: '12px', fontFamily: '"Plus Jakarta Sans", sans-serif' }}>Reach level 5 in a game</Typography>
                  </Box>
                </Box>
                <Button onClick={() => setShowOfferWarning(true)} sx={{ bgcolor: '#E6F4EA', color: '#0F763F', borderRadius: '10px', px: 2, py: 0.5, fontWeight: 700, textTransform: 'none' }}>Start</Button>
              </Box>
              {/* Offer C */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1.25, borderBottom: '1px solid #F0F0F0' }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Box sx={{ width: 38, height: 38, bgcolor: '#A05C39', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 'bold', fontSize: '18px', fontFamily: '"Plus Jakarta Sans", sans-serif' }}>C</Box>
                  <Box>
                    <Typography sx={{ color: '#000', fontWeight: 800, fontSize: '15px', fontFamily: '"Plus Jakarta Sans", sans-serif' }}>Offer C • ₹15</Typography>
                    <Typography sx={{ color: '#666', fontSize: '12px', fontFamily: '"Plus Jakarta Sans", sans-serif' }}>Sign up on a shopping app</Typography>
                  </Box>
                </Box>
                <Button onClick={() => setShowOfferWarning(true)} sx={{ bgcolor: '#E6F4EA', color: '#0F763F', borderRadius: '10px', px: 2, py: 0.5, fontWeight: 700, textTransform: 'none' }}>Start</Button>
              </Box>
              {/* Offer D */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1.25 }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Box sx={{ width: 38, height: 38, bgcolor: '#673AB7', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 'bold', fontSize: '18px', fontFamily: '"Plus Jakarta Sans", sans-serif' }}>D</Box>
                  <Box>
                    <Typography sx={{ color: '#000', fontWeight: 800, fontSize: '15px', fontFamily: '"Plus Jakarta Sans", sans-serif' }}>Offer D • ₹20</Typography>
                    <Typography sx={{ color: '#666', fontSize: '12px', fontFamily: '"Plus Jakarta Sans", sans-serif' }}>Complete a 3-minute survey</Typography>
                  </Box>
                </Box>
                <Button onClick={() => setShowOfferWarning(true)} sx={{ bgcolor: '#E6F4EA', color: '#0F763F', borderRadius: '10px', px: 2, py: 0.5, fontWeight: 700, textTransform: 'none' }}>Start</Button>
              </Box>
            </Box>
          </Container>
          </Box>

          {/* Other offers unlock only after the current one is finished */}
          <Snackbar
            open={showOfferWarning}
            autoHideDuration={4000}
            onClose={() => setShowOfferWarning(false)}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            sx={{ top: 'calc(16px + env(safe-area-inset-top)) !important' }}
          >
            <Alert severity="warning" variant="filled" onClose={() => setShowOfferWarning(false)} sx={{ width: '100%', borderRadius: '12px', fontFamily: font }}>
              Complete Offer 1 first. Tap <b>Get Money</b> to finish it.
            </Alert>
          </Snackbar>
        </>
      ) : (
        <Container maxWidth="sm" sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', px: 2, pt: 2 }}>
        
        {/* Top Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, pt: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton onClick={handleBack} sx={{ color: '#000', p: 0, mr: 1.5 }}>
              <ArrowBackIosNewIcon sx={{ fontSize: 20 }} />
            </IconButton>
            <Typography sx={{ color: '#000', fontWeight: 800, fontSize: '16px', fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
              {getOfferDisplayName(formName)}
            </Typography>
          </Box>
          <Box sx={{ bgcolor: '#C9F23C', px: 1.5, py: 0.5, borderRadius: '8px' }}>
            <Typography sx={{ color: '#000', fontWeight: 900, fontSize: '14px', fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
              ₹{offerAmount}
            </Typography>
          </Box>
        </Box>

        {/* Heading Section */}
        <Typography sx={{ color: '#0F231A', fontSize: '30px', fontWeight: 700, lineHeight: 1.1, mb: 2.5, letterSpacing: '-1px', fontFamily: '"Moan Slight", sans-serif' }}>
          Install, open, then send us a screenshot
        </Typography>

        {/* Error Alert */}
        {errorMessage && (
          <Alert
            severity="error"
            onClose={() => setErrorMessage('')}
            sx={{ mb: 3, borderRadius: '12px', '& .MuiAlert-message': { width: '100%' } }}
          >
            {errorMessage}
          </Alert>
        )}

        {/* Steps Card */}
        <Box sx={{ bgcolor: '#fff', borderRadius: '24px', p: 2.5, mb: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
          {/* Step 1 */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Box sx={{ width: 28, height: 28, bgcolor: '#0F231A', borderRadius: '50%', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: 800, flexShrink: 0, fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
              1
            </Box>
            <Box>
              <Typography sx={{ color: '#000', fontSize: '15px', fontWeight: 700, mb: 1.5, fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
                Install {getOfferDisplayName(formName)} from the App Store
              </Typography>
              {/* A real link, not window.location: iOS only hands a user-tapped link to the App Store app */}
              {/* No new tab in Safari: it would leave an App Store preview tab open when the user comes back */}
              <Button
                variant="contained"
                startIcon={<AppleIcon />}
                component="a"
                href={appStoreHref || undefined}
                target={inAppBrowser ? '_blank' : undefined}
                rel="noopener noreferrer"
                onClick={(e: React.MouseEvent) => {
                  if (!appStoreHref) {
                    e.preventDefault();
                    setErrorMessage('App Store link not found. Please refresh the page and try again.');
                    return;
                  }
                  if (inAppBrowser) {
                    // If the App Store opened, this page went to the background. Still visible means it was blocked.
                    setAppStoreBlocked(false);
                    clearTimeout(appStoreCheckTimer.current);
                    appStoreCheckTimer.current = setTimeout(() => {
                      if (document.visibilityState === 'visible') setAppStoreBlocked(true);
                    }, 1500);
                  }
                }}
                sx={{ 
                  bgcolor: '#0F231A !important', 
                  color: '#fff !important', 
                  borderRadius: '10px', 
                  textTransform: 'none', 
                  fontWeight: 600,
                  fontSize: '14px',
                  boxShadow: 'none',
                  px: 2,
                  py: 1,
                  fontFamily: '"Plus Jakarta Sans", sans-serif'
                }}
              >
                Open App Store
              </Button>

              {appStoreBlocked && (
                <Alert severity="info" onClose={() => setAppStoreBlocked(false)} sx={{ mt: 1.5, borderRadius: '12px', '& .MuiAlert-message': { width: '100%' } }}>
                  <Typography sx={{ fontSize: '14px', fontWeight: 700, mb: 0.5, fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
                    {inAppBrowser} can't open the App Store
                  </Typography>
                  <Typography sx={{ fontSize: '13px', lineHeight: 1.5, fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
                    1. Tap <MoreHorizIcon sx={{ fontSize: 16, verticalAlign: 'text-bottom' }} /> in the top-right corner<br />
                    2. Tap <b>Open in external browser</b><br />
                    3. Tap <b>Open App Store</b> again
                  </Typography>
                  <Button onClick={handleCopyLink} size="small" sx={{ mt: 1, p: 0, minWidth: 0, color: '#0F763F', fontWeight: 700, textTransform: 'none', fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
                    {linkCopied ? 'Link copied. Paste it in Safari' : 'Or copy the link'}
                  </Button>
                </Alert>
              )}
            </Box>
          </Box>

          <Box sx={{ height: '1px', bgcolor: '#F0F0F0', my: 2 }} />

          {/* Step 2 */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Box sx={{ width: 28, height: 28, bgcolor: '#0F231A', borderRadius: '50%', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: 800, flexShrink: 0, fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
              2
            </Box>
            <Box sx={{ pt: 0.5 }}>
              <Typography sx={{ color: '#000', fontSize: '15px', fontWeight: 700, fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
                Open the app and create an account
              </Typography>
            </Box>
          </Box>

          <Box sx={{ height: '1px', bgcolor: '#F0F0F0', my: 2 }} />

          {/* Step 3 */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ width: 28, height: 28, bgcolor: '#0F231A', borderRadius: '50%', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: 800, flexShrink: 0, fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
              3
            </Box>
            <Box sx={{ pt: 0.5 }}>
              <Typography sx={{ color: '#000', fontSize: '15px', fontWeight: 700, fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
                Screenshot the app's home screen and upload it below
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Which screenshot to upload */}
        <Typography sx={{ color: '#000', fontSize: '17px', fontWeight: 800, fontFamily: font }}>
          Which screenshot to upload?
        </Typography>
        <Typography sx={{ color: '#666', fontSize: '13px', mb: 1.5, fontFamily: font }}>
          Check the examples below before uploading.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.25, mb: 2.5 }}>
          <ScreenshotExample
            correct={false}
            image={incorrectExample}
            caption="App Store listing or download screen"
          />
          <ScreenshotExample
            correct
            image={correctExample}
            caption={`${getOfferDisplayName(formName).replace(/ App$/, '') || 'The app'} opened in the app (home screen visible)`}
          />
        </Box>

        {/* Upload Area */}
        <Box
          component="label"
          ref={uploadAreaRef}
          sx={{
            border: `2px dashed ${uploadError ? '#DC2626' : file || highlightUpload ? '#0F763F' : '#ccc'}`,
            boxShadow: highlightUpload ? '0 0 0 4px rgba(15, 118, 63, 0.2)' : 'none',
            transition: 'border-color 0.2s, box-shadow 0.2s',
            borderRadius: '20px', 
            p: 2.5, 
            mb: uploadError ? 1 : 2,
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center',
            cursor: 'pointer',
            bgcolor: file ? '#F3FAF5' : 'transparent',
            position: 'relative'
          }}
        >
          <input
            type="file"
            hidden
            accept="image/*"
            onChange={(e) => {
              const selected = e.target.files?.[0];
              e.target.value = '';
              if (selected) handleFileSelected(selected);
            }}
          />
          {isCheckingFile ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={18} sx={{ color: '#0F763F' }} />
              <Typography sx={{ color: '#000', fontSize: '14px', fontWeight: 700, fontFamily: font }}>Checking screenshot...</Typography>
            </Box>
          ) : file ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
              {previewUrl && (
                <Box component="img" src={previewUrl} alt="Selected screenshot" sx={{ width: 44, height: 80, objectFit: 'cover', objectPosition: 'top', borderRadius: '8px', border: '1px solid #ddd', flexShrink: 0 }} />
              )}
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ color: '#0F763F', fontWeight: 700, fontSize: '15px', fontFamily: font, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  Selected: {file.name}
                </Typography>
                <Typography sx={{ color: '#666', fontSize: '12px', fontFamily: font }}>Tap to choose a different screenshot</Typography>
              </Box>
            </Box>
          ) : (
            <>
              <ImageIcon sx={{ fontSize: 28, color: '#000', mb: 0.5 }} />
              <Typography sx={{ color: '#000', fontSize: '15px', fontWeight: 800, mb: 0.25, fontFamily: font }}>
                Tap to upload screenshot
              </Typography>
              <Typography sx={{ color: '#666', fontSize: '12px', fontFamily: font }}>
                From your Photos • PNG or JPG
              </Typography>
            </>
          )}
        </Box>
        {uploadError && (
          <Typography sx={{ color: '#DC2626', fontSize: '13px', lineHeight: 1.4, mb: 2, fontFamily: font }}>
            {uploadError}
          </Typography>
        )}

        <Box sx={{ flexGrow: 1 }} />

        {/* Submit Button: sticky so it's visible on every iPhone size */}
        <StickyActionBar>
          <Button
            variant="contained"
            fullWidth
            onClick={handleSubmit}
            // Not disabled without a file, so the tap can lead the user to the upload area; styled as disabled instead
            disabled={isSubmitting || isCheckingFile}
            sx={{
              bgcolor: `${file ? '#0F763F' : '#A5D6B6'} !important`,
              color: '#fff',
              py: 1.8,
              borderRadius: '16px',
              fontSize: '17px',
              fontWeight: 800,
              textTransform: 'none',
              boxShadow: 'none',
              fontFamily: font,
              '&:hover': {
                bgcolor: `${file ? '#0d6535' : '#A5D6B6'} !important`,
                boxShadow: 'none',
              },
              '&:disabled': {
                bgcolor: '#A5D6B6 !important',
                color: '#fff'
              }
            }}
          >
            {isSubmitting ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1, color: '#fff' }} />
                Submitting...
              </>
            ) : (
              'Submit screenshot'
            )}
          </Button>
        </StickyActionBar>

        {/* Outside the sticky bar, so it only shows once the user scrolls to the end */}
        {/* Negative margin pulls it into the bar's bottom padding (12px + home-indicator inset) */}
        <Typography sx={{ color: '#666', fontSize: '12px', textAlign: 'center', mt: 'calc(-6px - env(safe-area-inset-bottom))', pb:'calc(16px + env(safe-area-inset-bottom))', fontFamily: font }}>
          Our team checks every screenshot before paying out
        </Typography>

        </Container>
      )}
    </Box>
  );
}

